/**
 * Alpine component powering the on-site appointment booking widget.
 *
 * Flow: choose a service -> choose a location (only if the service has more
 * than one) -> choose a staff member (unless skipped/locked) -> pick a
 * date/time slot -> fill in details -> submit.
 *
 * All 10to8 API calls go through our own BookingController proxy
 * (app/Http/Controllers/BookingController.php) so the 10to8 API key is
 * never exposed to the browser.
 *
 * IMPORTANT: 10to8's API is Tastypie-style — service/staff/location/question
 * references must be the full `resource_uri` hyperlink (e.g.
 * ".../service/1234/"), NOT the bare numeric ID. `resource_id` (extracted
 * server-side) is only used for display/config matching (e.g. the
 * `booking_service_ids`/`booking_staff_id` blueprint fields).
 *
 * NOTE: 10to8 has no "no preference" concept server-side - every slot
 * belongs to a specific staff member, even when `/slot` is queried without
 * a `staff` filter. Each result includes its own `staff` resource_uri, which
 * is what we submit to `/book` when the customer didn't pick a staff member.
 */
export default function bookingForm(dataset) {
    return {
        // ── Config, read from the container's data-* attributes ──
        skipStaffSelect:
            dataset.skipStaffSelect === "1" ||
            dataset.skipStaffSelect === "true",
        lockedStaffId: dataset.staffId || null,
        allowedServiceIds: dataset.serviceIds
            ? dataset.serviceIds
                  .split(",")
                  .map((id) => id.trim())
                  .filter(Boolean)
            : null,

        // ── Data ──
        loading: true,
        error: "",
        services: [],
        availableServices: [],
        staff: [],

        // ── Wizard state ──
        step: "services", // services | location | staff | slots | details | complete
        selectedService: null,
        selectedLocation: null,
        selectedStaff: null, // null = "no preference"

        // ── Calendar / slots ──
        monthCursor: null,
        slotsByDate: {},
        slotsLoading: false,
        slotsError: "",
        selectedSlot: null,

        // ── Details form ──
        form: {
            customer_name: "",
            customer_email: "",
            customer_phone_number: "",
            answers: {},
        },
        // Custom field, not part of the 10to8 API - never sent to /booking/complete.
        privacyAccepted: false,
        submitting: false,
        submitError: "",

        async init() {
            this.monthCursor = this.startOfMonth(new Date());

            try {
                const response = await fetch("/booking/services", {
                    headers: { Accept: "application/json" },
                });

                if (!response.ok) {
                    throw new Error();
                }

                let services = await response.json();

                if (this.allowedServiceIds) {
                    services = services.filter((service) =>
                        this.allowedServiceIds.includes(
                            String(service.resource_id),
                        ),
                    );
                }

                if (this.lockedStaffId) {
                    services = services.filter((service) =>
                        service.staff.some(
                            (member) =>
                                String(member.resource_id) ===
                                String(this.lockedStaffId),
                        ),
                    );
                }

                // Shuffle so the same staff member isn't always shown first.
                services.forEach((service) => {
                    service.staff = this.shuffle(service.staff);
                });

                this.services = services;
                this.availableServices = services;

                // Unique staff across all available services (used to render
                // the staff-select step, and to resolve the locked staff member).
                const seen = new Set();
                this.staff = services
                    .flatMap((service) => service.staff)
                    .filter((member) => {
                        if (seen.has(member.resource_id)) return false;
                        seen.add(member.resource_id);
                        return true;
                    });
            } catch (e) {
                this.error =
                    "There was a problem loading the booking form. Please refresh the page and try again, or contact us directly.";
            } finally {
                this.loading = false;
            }
        },

        selectService(service) {
            this.selectedService = service;
            this.selectedSlot = null;
            this.selectedLocation = null;

            const locations = service.locations ?? [];

            if (locations.length > 1) {
                this.step = "location";
            } else {
                this.selectedLocation = locations[0] ?? null;
                this.afterLocationChosen();
            }
        },

        selectLocation(location) {
            this.selectedLocation = location;
            this.afterLocationChosen();
        },

        afterLocationChosen() {
            const service = this.selectedService;

            if (this.lockedStaffId) {
                this.selectedStaff =
                    service.staff.find(
                        (member) =>
                            String(member.resource_id) ===
                            String(this.lockedStaffId),
                    ) ?? null;
                this.goToSlots();
            } else if (this.skipStaffSelect) {
                this.selectedStaff = null;
                this.goToSlots();
            } else {
                this.step = "staff";
            }
        },

        selectStaff(staffMemberOrNull) {
            this.selectedStaff = staffMemberOrNull;
            this.goToSlots();
        },

        // Fisher-Yates shuffle, returns a new array.
        shuffle(array) {
            const result = [...array];
            for (let i = result.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [result[i], result[j]] = [result[j], result[i]];
            }
            return result;
        },

        // Staff photos live in public/images/staff/First-Last.png (spaces in
        // the 10to8 staff name replaced with hyphens). Falls back to a plain
        // icon via the <img>'s @error handler if no matching file exists.
        staffPhotoUrl(member) {
            if (!member?.name) return "";
            return `/images/staff/${member.name.trim().replace(/\s+/g, "-")}.png`;
        },

        changeService() {
            this.step = "services";
            this.selectedSlot = null;
            this.selectedLocation = null;
            if (!this.lockedStaffId) {
                this.selectedStaff = null;
            }
        },

        changeStaff() {
            this.step = "staff";
            this.selectedSlot = null;
        },

        changeSlot() {
            this.step = "slots";
            this.selectedSlot = null;
        },

        goToSlots() {
            this.step = "slots";
            this.monthCursor = this.startOfMonth(new Date());
            this.fetchSlots();
        },

        async prevMonth() {
            const today = this.startOfMonth(new Date());
            const prev = new Date(
                this.monthCursor.getFullYear(),
                this.monthCursor.getMonth() - 1,
                1,
            );
            if (prev < today) return;
            this.monthCursor = prev;
            await this.fetchSlots();
        },

        async nextMonth() {
            this.monthCursor = new Date(
                this.monthCursor.getFullYear(),
                this.monthCursor.getMonth() + 1,
                1,
            );
            await this.fetchSlots();
        },

        get isCurrentMonth() {
            const today = this.startOfMonth(new Date());
            return (
                this.monthCursor.getFullYear() === today.getFullYear() &&
                this.monthCursor.getMonth() === today.getMonth()
            );
        },

        get monthLabel() {
            return this.monthCursor.toLocaleDateString(undefined, {
                month: "long",
                year: "numeric",
            });
        },

        get slotDates() {
            return Object.keys(this.slotsByDate).sort();
        },

        async fetchSlots() {
            this.slotsLoading = true;
            this.slotsError = "";
            this.slotsByDate = {};

            const today = new Date();
            const start = this.isCurrentMonth
                ? today
                : this.startOfMonth(this.monthCursor);
            const end = new Date(
                this.monthCursor.getFullYear(),
                this.monthCursor.getMonth() + 1,
                1,
            );

            const params = new URLSearchParams({
                start_date: this.formatDate(start),
                end_date: this.formatDate(end),
                service: this.selectedService.resource_uri,
                location: this.selectedLocation.resource_uri,
            });

            if (this.selectedStaff) {
                params.set("staff", this.selectedStaff.resource_uri);
            }

            try {
                const response = await fetch(
                    `/booking/slots?${params.toString()}`,
                    {
                        headers: { Accept: "application/json" },
                    },
                );

                if (!response.ok) {
                    throw new Error();
                }

                const data = await response.json();

                // 10to8 returns { results: [{ start_datetime, end_datetime, staff }, ...] }.
                const slots = data.results ?? data ?? [];

                const byDate = {};
                slots.forEach((slot) => {
                    const date = (
                        slot.start_datetime ??
                        slot.start_date ??
                        ""
                    ).slice(0, 10);
                    if (
                        !date ||
                        date < params.get("start_date") ||
                        date >= params.get("end_date")
                    ) return;
                    (byDate[date] ??= []).push(slot);
                });

                this.slotsByDate = byDate;
            } catch (e) {
                this.slotsError =
                    "There was a problem loading available times. Please try again.";
            } finally {
                this.slotsLoading = false;
            }
        },

        formatServiceName(name) {
            if (!name) return "";
            return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
        },

        formatSlotTime(slot) {
            const dt = slot.start_datetime ?? slot.start_date;
            if (!dt) return "";
            return new Date(dt).toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
            });
        },

        formatSlotEndTime(slot) {
            const dt = slot.end_datetime ?? slot.end_date;
            if (!dt) return "";
            return new Date(dt).toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
            });
        },

        formatDayHeading(dateString) {
            return new Date(`${dateString}T00:00:00`).toLocaleDateString(
                undefined,
                {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                },
            );
        },

        selectSlot(slot) {
            this.selectedSlot = slot;
            this.form.answers = {};
            (this.selectedService.booking_questions ?? []).forEach(
                (question) => {
                    // "radio" questions expect a real boolean answer, not text.
                    this.form.answers[question.resource_uri] =
                        question.type === "radio" ? null : "";
                },
            );
            this.step = "details";
        },

        get selectedSlotLabel() {
            if (!this.selectedSlot) return "";
            return `${this.formatDayHeading(this.selectedSlot.start_datetime.slice(0, 10))}, ${this.formatSlotTime(this.selectedSlot)}`;
        },

        // "School Name"/"Trust Name" depending on the selected service's name, falling
        // back to the generic label when neither word appears in it.
        get customerNameLabel() {
            const name = (this.selectedService?.name ?? "").toLowerCase();
            if (name.includes("trust")) return "Trust Name";
            if (name.includes("school")) return "School Name";
            return "School/Trust Name";
        },

        async submitBooking() {
            this.submitError = "";

            if (!this.form.customer_name.trim()) {
                this.submitError = "Please enter your school or trust name.";
                return;
            }

            if (
                this.selectedService.email_required &&
                !this.form.customer_email.trim()
            ) {
                this.submitError = "Please enter your email address.";
                return;
            }

            if (
                this.selectedService.phone_required &&
                !this.form.customer_phone_number.trim()
            ) {
                this.submitError = "Please enter your phone number.";
                return;
            }

            for (const question of this.selectedService.booking_questions ??
                []) {
                if (
                    question.required &&
                    !String(
                        this.form.answers[question.resource_uri] ?? "",
                    ).trim()
                ) {
                    this.submitError = "Please answer all required questions.";
                    return;
                }
            }

            if (!this.privacyAccepted) {
                this.submitError =
                    "Please give permission to use your information to continue.";
                return;
            }

            this.submitting = true;

            try {
                const response = await fetch("/booking/complete", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({
                        start_datetime: this.selectedSlot.start_datetime,
                        customer_name: this.form.customer_name,
                        customer_email: this.form.customer_email,
                        customer_phone_number: this.form.customer_phone_number,
                        service: this.selectedService.resource_uri,
                        location: this.selectedLocation.resource_uri,
                        // No staff preference chosen: fall back to whichever staff member the slot itself belongs to.
                        staff:
                            this.selectedStaff?.resource_uri ??
                            this.selectedSlot?.staff ??
                            null,
                        answers: Object.entries(this.form.answers).map(
                            ([question, answer]) => ({
                                question,
                                answer,
                            }),
                        ),
                    }),
                });

                const data = await response.json().catch(() => null);

                if (!response.ok) {
                    throw new Error(
                        data?.message ??
                            "Something went wrong submitting your booking.",
                    );
                }

                this.step = "complete";
            } catch (e) {
                this.submitError =
                    e.message ||
                    "Something went wrong submitting your booking. Please try again.";
            } finally {
                this.submitting = false;
            }
        },

        startOfMonth(date) {
            return new Date(date.getFullYear(), date.getMonth(), 1);
        },

        formatDate(date) {
            return [
                date.getFullYear(),
                String(date.getMonth() + 1).padStart(2, "0"),
                String(date.getDate()).padStart(2, "0"),
            ].join("-");
        },
    };
}
